import {FileDocument} from '../../../../../../displaySet/model/AttributeValue'
import {FieldArray, getIn, useFormikContext} from 'formik'
import {ItemCreationForm} from '../../../../../model/NewItemInput'
import {ChangeEvent, ComponentType, ReactNode, useState} from 'react'
import {Button, Link, MenuItem, Select, TextField} from '@mui/material'
import {DateRangeOutlined, Link as LinkIcon} from '@mui/icons-material'
import * as FileSaver from 'file-saver'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import './SharedComponents.scss'
import {BodySmall} from '../../../../../../genericComponents/typographie/UIKitV2/BodySmall'
import {InfoTooltip} from '../../../../../../genericComponents/customInfoToolTip/InfoTooltip'
import UploadFileLoadingButton from '../../../../genericComponents/UploadFileLoadingButton'
import {createDateObjectByFormats, dateFormat} from '../../../../../../genericComponents/date/DateService'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {DatePicker} from '@mui/x-date-pickers'
import {Dayjs} from 'dayjs'
import CustomSwitch from '../../../../../../genericComponents/customSwitch/CustomSwitch'
import {Subheader} from '../../../../../../genericComponents/typographie/UIKitV2/Subheader'
import {Label} from '../../../../../../genericComponents/typographie/Label'

export const AddedFiles = ({target}: { target: string }) => {
    const formik = useFormikContext<ItemCreationForm>()
    const files: FileDocument[] = (getIn(formik.values, target) as FileDocument[]) ?? []

    const getFile = (localFile?: File) => {
        if (!localFile) return
        FileSaver.saveAs(localFile, localFile.name)
    }

    if (files.length === 0) return <></>

    return <FieldArray name={target} render={({remove}) => (<>
        {files.map((fileDocument, index) => {
            const urlPath = `${target}[${index}].externalUrl`
            const error = getIn(formik.errors, urlPath) as string | undefined
            const touched = getIn(formik.touched, urlPath)
            return <div className="added-files">
                {fileDocument.isFile ? <>
                    <BodySmall>File:</BodySmall>
                    <BodySmall component="div" className="file-details">
                        <Link className="link" onClick={() => getFile(fileDocument.localFile)}>
                            {fileDocument.file?.name ?? '-'}
                        </Link>
                        <Button className="delete-icon" onClick={() => remove(index)}>
                            <DeleteForeverOutlinedIcon/>
                        </Button>
                    </BodySmall>
                </> : <>
                    <BodySmall>URL:</BodySmall>
                    <BodySmall component="div" className="file-details">
                        <TextField
                            fullWidth
                            className="file-url-input"
                            name={urlPath}
                            id={urlPath}
                            value={getIn(formik.values, urlPath) ?? ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(touched && error)}
                            helperText={touched ? error ?? '' : ''}
                        />
                        <Button className="delete-icon" onClick={() => remove(index)}>
                            <DeleteForeverOutlinedIcon/>
                        </Button>
                    </BodySmall>
                </>}
            </div>
        })}
    </>)}/>
}

export const AddFileField = ({target, title}: { target: string, title?: string }) => {
    return <FieldArray name={target} render={({push}) => (
        <div className="service-form-field service-form-actions-section">
            {title && <Label>{title}</Label>}
            <div className="service-form-actions">
                <UploadFileLoadingButton
                    name={target}
                    className="new-file-button"
                    variant="outlined"
                    parseFile={(event: ChangeEvent<HTMLInputElement>) => {
                        if (event.target?.files?.length) {
                            const tempFile = event.target.files[0]
                            const newDocument: FileDocument = {
                                file: {
                                    name: tempFile.name,
                                    size: tempFile.size,
                                    uri: null
                                },
                                isFile: true,
                                localFile: tempFile
                            }
                            push(newDocument)
                        }
                    }}
                />

                <Button component="label" variant="outlined" color="primary"
                        className="new-file-attribute-button"
                        endIcon={<LinkIcon/>}
                        onClick={() => {
                            const newDocument: FileDocument = {
                                externalUrl: 'https://',
                                isFile: false
                            }
                            push(newDocument)
                        }}>Add URL
                </Button>
            </div>
        </div>
    )}/>
}

export const TextWithTooltip = ({label, tooltip, TypoComponent = BodySmall}: {
    label: string | ReactNode,
    tooltip?: string,
    TypoComponent?: ComponentType<{ children?: ReactNode }>
}) => <div className="text-with-tooltip">
    <TypoComponent>{label}</TypoComponent>
    {tooltip && <TypoComponent><InfoTooltip title={tooltip} size="inherit"/></TypoComponent>}
</div>


type SelectFieldProps<T extends string> = {
    target: string
    options: readonly T[]
    placeholder?: string
};

export const SelectField = <T extends string, >({
                                                    target,
                                                    options,
                                                    placeholder = 'Select an option'
                                                }: SelectFieldProps<T>) => {
    const formik = useFormikContext<ItemCreationForm>()

    return (
        <Select
            id={target}
            name={target}
            fullWidth
            value={(getIn(formik.values, target) as T) ?? ''}
            onBlur={formik.handleBlur}
            onChange={(e) => {
                const val = e.target.value as T
                formik.setFieldValue(target, val)
            }}
            displayEmpty
            renderValue={selected => !selected ? <span className="placeholder">{placeholder}</span> : selected}
        >
            {options.map((option) => <MenuItem value={option} key={option}>{option}</MenuItem>)}
        </Select>
    )
}

export const DateField = ({target}: { target: string }) => {
    const formik = useFormikContext<ItemCreationForm>()

    const getDateValueObject = (dateValue: any) => {
        return createDateObjectByFormats(dateValue, [dateFormat])
    }

    const DatePickerIcon = () => {
        return <div className="date-attribute-datepicker-icon"><DateRangeOutlined/></div>
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                value={getIn(formik.values, target) === '' ? null : getDateValueObject(getIn(formik.values, target))}
                format={dateFormat}
                onChange={(value: Dayjs | null) => {
                    formik.setFieldValue(target, value?.format(dateFormat))
                }}
                slots={{
                    openPickerIcon: DatePickerIcon
                }}
                slotProps={{
                    textField: {
                        error: getIn(formik.touched, target) && Boolean(getIn(formik.errors, target)),
                        helperText: getIn(formik.touched, target) && getIn(formik.errors, target) as string
                    }
                }}
            />
        </LocalizationProvider>
    )
}

export const SectionWithSwitch = ({children, className = '', tooltip, target, label}: {
    children: ReactNode,
    className?: string,
    tooltip?: string
    target: string,
    label: string
}) => {
    const [displayFormFields, setDisplayFormFields] = useState(false)
    const formik = useFormikContext<ItemCreationForm>()

    const handleToggle = (e: ChangeEvent<HTMLInputElement>) => {
        setDisplayFormFields(e.target.checked)
        if (!e.target.checked) formik.setFieldValue(target, [])
    }

    return <div className={`service-switch ${className}`}>
        <CustomSwitch isChecked={displayFormFields}
                      toggleChecked={handleToggle}
                      disabled={false}
                      checkedLabel={<TextWithTooltip label={label} tooltip={tooltip}
                                                     TypoComponent={Subheader}/>}
                      uncheckedLabel={<TextWithTooltip label={label} tooltip={tooltip}
                                                       TypoComponent={Subheader}/>}
        />
        {displayFormFields && <>{children}</>}
    </div>
}
